import { playerPathwayImage } from '@/assets/images';
import { Divider, Scroll, Stack } from '@/components/layout';
import { AppHeader } from '@/components/layout/app-header';
import {
  Avatar,
  Button,
  Checkbox,
  Collapsible,
  FormInput,
  Image,
  Level,
  RadioGroup,
  SvgIcon,
} from '@/components/ui';
import { Text } from '@/components/ui/text';
import { TextInput } from '@/components/ui/text-input';
import { useScaling } from '@/hooks';
import { envPublic } from '@/libs/config';
import { NavigationService } from '@/navigations/navigation-ref';
import { Device } from '@/theme/scaling';
import { theme } from '@/theme/theme';
import { fontFamilies } from '@/theme/typography';
import { StyleSheet } from 'react-native';
import { KeyboardAvoidingView } from 'react-native-keyboard-controller';

/**
 * Component screen
 * This screen is used for component development purposes only
 * @returns Component screen component
 */
export function ComponentScreen() {
  const { scale } = useScaling();
  return (
    <KeyboardAvoidingView behavior="padding" style={styles.keyboardAvoidingView}>
      <AppHeader>
        <AppHeader.Left>
          <Text>Left</Text>
        </AppHeader.Left>
        <AppHeader.Title>
          <Text variant="Font Inter/Body/Bold 16px" numberOfLines={1} ellipsizeMode="tail">
            This is development title
          </Text>
        </AppHeader.Title>
        <AppHeader.Right>
          <Text>Right</Text>
        </AppHeader.Right>
      </AppHeader>
      <Scroll align="center" justify="center" width="100%" flexGrow={1}>
        <Text>Component screen</Text>
        <Stack paddingX={scale(16)} flex={1} align="flex-start" gap={scale(16)} width="100%">
          {/** TODO: Should apply encrypt env props */}
          <Text>ENV: {envPublic.environment || 'No environment'}</Text>

          {/** ----------------------------- Avatar component ----------------------------- */}
          <Text variant="Font Inter/Subtitle/Medium 14px">Avatar</Text>
          <Avatar size={scale(48)}>
            <Avatar.Image
              source={{
                uri: 'https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcTfE8XWOVe86hLGi8m9mgPTsva_KWjTHbT9iQ&s',
              }}
            />
            <Avatar.Label>PROFILE</Avatar.Label>
          </Avatar>
          <Divider spacing={scale(16)} />

          {/** ----------------------------- Checkbox component ----------------------------- */}
          <Text variant="Font Inter/Subtitle/Medium 14px">Checkbox</Text>
          <Checkbox label="Checkbox label" />
          <Divider spacing={scale(16)} />

          {/** ----------------------------- SvgIcon component ----------------------------- */}
          <Text variant="Font Inter/Subtitle/Medium 14px">
            SvgIcon – icon from internal svg set
          </Text>
          <SvgIcon name="IcCheckboxChecked" />
          <Divider spacing={scale(16)} />

          {/** ----------------------------- Button component ----------------------------- */}
          <Text variant="Font Inter/Subtitle/Medium 14px">Button</Text>
          <Stack direction="row" gap={16}>
            <Button>
              <Button.Icon
                name="IcCheckboxChecked"
                styleWrapper={{ width: scale(24), height: scale(24) }}
              />
              <Button.Text>Open drawer</Button.Text>
            </Button>
            <Button>
              <Button.Text>Open public stack</Button.Text>
            </Button>
          </Stack>
          <Divider spacing={scale(16)} />

          {/** ----------------------------- Text component ----------------------------- */}
          <Text variant="Font Inter/Subtitle/Medium 14px">Text component</Text>
          <Text variant="Font Inter/Body/Bold 16px">Variant type component</Text>
          <Text
            fontFamily={fontFamilies.inter}
            fontSize={scale(12)}
            fontWeight="400"
            lineHeight={scale(26)}
            letterSpacing={0}>
            All custom props text
          </Text>
          <Divider spacing={scale(16)} />

          {/** ----------------------------- Image component ----------------------------- */}
          <Text variant="Font Inter/Subtitle/Medium 14px">
            Image – TurboImage wrapper with cachePolicy &amp; resizeMode
          </Text>
          <Image
            source={playerPathwayImage}
            cachePolicy="immutable"
            width={scale(240)}
            height={scale(260)}
            resizeMode="cover"
          />
          <Divider spacing={scale(16)} />

          {/** ----------------------------- Level component ----------------------------- */}
          <Text variant="Font Inter/Subtitle/Medium 14px">
            Level – badge displaying level with token-based color
          </Text>
          <Level style={{ marginTop: scale(16) }} value={1} alignSelf="center" />
          <Divider spacing={16} />

          {/** ----------------------------- TextInput component ----------------------------- */}
          <Text variant="Font Inter/Subtitle/Medium 14px">
            TextInput – field with left/right icon for keyboard testing
          </Text>
          <TextInput>
            <TextInput.Content>
              <TextInput.Icon position="left" name="IcCheckboxChecked" />
              <TextInput.Field placeholder="Development text input for keyboard test" />
              <TextInput.Icon position="right" name="IcCheckboxChecked" />
            </TextInput.Content>
          </TextInput>
          <Divider spacing={scale(16)} />

          {/** ----------------------------- RadioGroup component ----------------------------- */}
          <Text variant="Font Inter/Subtitle/Medium 14px">RadioGroup</Text>
          <RadioGroup>
            <RadioGroup.Radio value="1" label="Radio 1" />
            <RadioGroup.Radio value="2" label="Radio 2" />
            <RadioGroup.Radio value="3" label="Radio 3" />
          </RadioGroup>
          <Divider spacing={scale(16)} />

          {/** ----------------------------- FormInput component ----------------------------- */}
          <Text variant="Font Inter/Subtitle/Medium 14px">TextFormInput</Text>
          <FormInput label="Username" iconProps={{ position: 'left', name: 'IcCheckboxChecked' }} />
          <Divider spacing={scale(16)} />

          {/** ----------------------------- Collapsible component ----------------------------- */}
          <Text variant="Font Inter/Subtitle/Medium 14px">Collapsible</Text>
          <Collapsible containerStyle={styles.collapsibleContainer}>
            <Collapsible.Trigger>Collapsible header</Collapsible.Trigger>
            <Collapsible.Content>
              <Stack paddingX={scale(16)}>
                <Text>Collapsible content</Text>
              </Stack>
              <Stack paddingX={scale(16)}>
                <Text>Collapsible content</Text>
              </Stack>
              <Stack paddingX={scale(16)}>
                <Text>Collapsible content</Text>
              </Stack>
              <Stack paddingX={scale(16)}>
                <Text>Collapsible content</Text>
              </Stack>
              <Stack paddingX={scale(16)}>
                <Text>Collapsible content</Text>
              </Stack>
            </Collapsible.Content>
          </Collapsible>
          <Divider spacing={scale(16)} />
        </Stack>
      </Scroll>
      <Stack align="center" justify="center" width="100%">
        <Button onPress={() => NavigationService.goBack()}>
          <Button.Text>Go back</Button.Text>
        </Button>
      </Stack>
    </KeyboardAvoidingView>
  );
}

const styles = StyleSheet.create({
  keyboardAvoidingView: {
    flex: 1,
    backgroundColor: theme.colors['Backgrounds/Primary'],
    marginBottom: Device.bottomHeight,
    width: '100%',
  },
  collapsibleContainer: {
    width: '100%',
  },
});
